import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  CursorPaginatedResponse,
  Customer,
  CustomerListData,
  CustomerListParams,
  CustomerPayload,
} from '@/libs/interfaces'
import { customersApiMock } from '@/libs/mocks/handlers/customers'

const customersApiReal = {
  create: async (payload: CustomerPayload): Promise<CreateApiResult<Customer>> => {
    return api
      .post<ApiResponse<Customer>>(URLs.Customers.create(payload.driver.orgID || ''), payload)
      .then((response) => ({
        data: response.data.data,
        status: response.status,
        message: response.message,
      }))
  },
  delete: async (payload: Partial<Customer>) => {
    const { orgID = '', driverID = '' } = payload
    return await api.delete(URLs.Customers.byID(orgID, driverID)).then((response) => response.data)
  },
  getByID: async (params: { orgID?: string; driverID?: string }): Promise<Partial<Customer>> => {
    const { orgID, driverID } = params
    return await api
      .get<ApiResponse<Partial<Customer>>>(URLs.Customers.byID(orgID ?? '', driverID ?? ''))
      .then((response) => response.data.data)
  },
  list: async (params: CustomerListParams): Promise<Customer[]> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<CustomerListData>>(URLs.Customers.list(orgID, query))
      .then((response) => response.data?.data.drivers || [])
  },
  listCursor: async (params: CustomerListParams): Promise<CursorPaginatedResponse<Customer>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<CustomerListData>>(URLs.Customers.list(orgID, query))
      .then((response) => response?.data?.data)

    return {
      data: response.drivers,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  update: async (payload: Partial<Customer>) => {
    const { orgID = '', driverID = '', ...patchData } = payload
    return await api
      .patch(URLs.Customers.byID(orgID, driverID), patchData)
      .then((response) => response.data)
  },
}

export const customersApi: typeof customersApiReal = __IS_MOCK_MODE__
  ? customersApiMock
  : customersApiReal
